import { useEffect, useMemo, useState } from "react";
import * as eventService from "../../services/eventService";
import * as userService from "../../services/userService";

type Props = {
  open: boolean;
  onClose: () => void;
  evento: eventService.EventoAdmin | null;
  onAssigned: () => void;
};

function AssignEventAdminModal({ open, onClose, evento, onAssigned }: Props) {
  const [users, setUsers] = useState<userService.UsuarioAdmin[]>([]);
  const [q, setQ] = useState("");
  const [rolEvento, setRolEvento] = useState("ADMIN");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setRolEvento("ADMIN");
    setSelectedUserId(null);
    setAssigning(false);

    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const data = await userService.listarUsuarios();
        setUsers(data);
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : "Error cargando usuarios");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (u) =>
        u.nombre.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
  }, [users, q]);

  if (!open || !evento) return null;
  const eventoId = evento.id;
  const adminsActuales = evento.admins ?? [];
  const assignedUserIds = new Set(adminsActuales.map((admin) => admin.usuarioId));
  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null;

  async function asignar() {
    if (!selectedUserId) {
      setErrorMsg("Selecciona un usuario.");
      return;
    }
    setAssigning(true);
    setErrorMsg(null);

    try {
      await eventService.asignarAdminEvento(eventoId, selectedUserId, rolEvento);
      await onAssigned();
      setAssigning(false);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error asignando usuario");
      setAssigning(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={() => {
          if (!assigning) onClose();
        }}
        aria-label="Cerrar"
      />

      <div className="relative w-full max-w-[900px] max-h-[90vh] rounded-2xl border border-white/10 bg-surface-darker shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-serif text-white">Gestionar responsables</h3>
            <p className="text-sm text-slate-400">
              Selecciona un usuario y define su rol para <span className="text-white font-medium">{evento.nombre}</span>.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={assigning}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            aria-label="Cerrar modal"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="px-6 py-6 space-y-5 overflow-y-auto min-h-0">
          <div className="rounded-xl border border-white/10 bg-background-dark p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h4 className="text-sm font-bold text-white">Responsables actuales</h4>
                <p className="text-xs text-slate-400">
                  {adminsActuales.length === 0
                    ? "Este evento todavía no tiene responsables."
                    : `${adminsActuales.length} usuario${adminsActuales.length === 1 ? "" : "s"} con acceso al evento.`}
                </p>
              </div>
              <span className="material-symbols-outlined text-primary">admin_panel_settings</span>
            </div>

            {adminsActuales.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm text-slate-400">
                Asigna al menos un responsable para que pueda administrar ceremonias, invitados y RSVP.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {adminsActuales.map((admin) => (
                  <div
                    key={admin.usuarioId}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <div className="size-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-primary text-[18px]">person</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white font-semibold truncate">{admin.nombre}</p>
                      <p className="text-xs text-slate-400 truncate">{admin.email}</p>
                    </div>
                    <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold">
                      {admin.rolEvento}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-7 relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
                search
              </span>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 pl-10 pr-4 transition-all duration-200"
                placeholder="Buscar usuario por nombre o correo..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={loading || assigning}
              />
            </div>

            <div className="md:col-span-3">
              <select
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={rolEvento}
                onChange={(e) => setRolEvento(e.target.value)}
                disabled={loading || assigning}
              >
                <option value="ADMIN">Administrador</option>
                <option value="OWNER">Propietario</option>
              </select>
            </div>

            <div className="md:col-span-2 flex items-center justify-end">
              <div className="w-full rounded-lg border border-white/10 bg-background-dark px-3 py-2 text-xs text-slate-400">
                <span className="text-white font-semibold">{filtered.length}</span> usuarios
              </div>
            </div>
          </div>

          {errorMsg && <p className="text-sm text-red-300">{errorMsg}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 rounded-xl border border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 bg-white/5">
                <p className="text-sm font-semibold text-white">Usuarios disponibles</p>
                <p className="text-xs text-slate-400">Elige una fila para preparar la asignación.</p>
              </div>
              <div className="max-h-[320px] overflow-y-auto">
              {loading && (
                <div className="p-4 text-sm text-slate-300">Cargando...</div>
              )}

              {!loading && filtered.length === 0 && (
                <div className="p-4 text-sm text-slate-300">
                  No se encontraron usuarios.
                </div>
              )}

              {!loading &&
                filtered.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedUserId(u.id)}
                    disabled={assigning}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex items-center gap-3 disabled:cursor-not-allowed ${
                      selectedUserId === u.id ? "bg-primary/15" : ""
                    }`}
                  >
                    <div className="size-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-[18px]">person</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white font-medium truncate">{u.nombre}</p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    {assignedUserIds.has(u.id) && (
                      <span className="rounded-full bg-emerald-500/10 text-emerald-400 px-2 py-0.5 text-[11px] font-semibold">
                        Asignado
                      </span>
                    )}
                    {selectedUserId === u.id && (
                      <span className="material-symbols-outlined text-primary text-[20px]">check_circle</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 rounded-xl border border-white/10 bg-background-dark p-4 flex flex-col gap-4">
              <div>
                <h4 className="text-sm font-bold text-white">Asignación preparada</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Revisa la selección antes de confirmar.
                </p>
              </div>

              {selectedUser ? (
                <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
                  <p className="text-sm font-semibold text-white">{selectedUser.nombre}</p>
                  <p className="text-xs text-slate-300 mt-1 break-words">{selectedUser.email}</p>
                  <div className="mt-3 inline-flex rounded-full bg-primary/15 text-primary px-2 py-1 text-xs font-bold">
                    Rol: {rolEvento}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm text-slate-400">
                  Selecciona un usuario de la lista para continuar.
                </div>
              )}

              <div className="text-xs text-slate-400">
                La asignación se guarda inmediatamente y la tarjeta del evento se actualizará al terminar.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={assigning}
              className="px-4 h-11 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors"
            >
              Cancelar
            </button>

            <button
              type="button"
              disabled={loading || assigning || !selectedUserId}
              onClick={asignar}
              className="px-5 h-11 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-all shadow-lg shadow-primary/20 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center justify-center gap-2">
                {assigning && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {assigning ? "Asignando..." : "Asignar responsable"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssignEventAdminModal;
